import { Link, router } from '@inertiajs/react'
import { useRef, useState } from 'react'
import SystemAdminLayout from '@/layouts/SystemAdminLayout'

interface CohortMember {
  user_id: number
  email: string
  name: string | null
  job_title: string | null
  account_id: number | null
  account_name: string | null
  confirmed: boolean
  created: boolean
  published: boolean
  first_response: boolean
}

interface Cohort {
  week_start: string
  signups: number
  confirmed: number
  created: number
  published: number
  first_response: number
  members: CohortMember[]
}

interface FunnelProps {
  cohorts: Cohort[]
  weeks: number
  instrumented_since: string | null
}

type StageKey = 'signups' | 'confirmed' | 'created' | 'published' | 'first_response'

interface Segment {
  label: string
  stageLabel: string
  members: CohortMember[]
}

const WEEK_OPTIONS = [4, 8, 12, 26]

const STAGES: { key: Exclude<StageKey, 'signups'>; label: string }[] = [
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'created', label: 'Created survey' },
  { key: 'published', label: 'Published' },
  { key: 'first_response', label: 'First response' },
]

const STAGE_LABELS: Record<StageKey, string> = {
  signups: 'Signups',
  confirmed: 'Confirmed',
  created: 'Created survey',
  published: 'Published',
  first_response: 'First response',
}

function formatWeek(iso: string): string {
  // Date-only ISO strings parse as UTC midnight; anchor to local time so the
  // label doesn't shift a day west of UTC.
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function stageMembers(members: CohortMember[], stage: StageKey): CohortMember[] {
  if (stage === 'signups') return members
  return members.filter((m) => m[stage])
}

interface SegmentCellProps {
  count: number
  base: number | null
  label: string
  stage: StageKey
  members: CohortMember[]
  onOpen: (label: string, stage: StageKey, members: CohortMember[]) => void
}

function SegmentCell({ count, base, label, stage, members, onOpen }: SegmentCellProps) {
  if (base === 0) return <td className="text-end text-base-content/40">—</td>
  if (count === 0 && stage === 'signups') return <td className="text-end text-base-content/40">—</td>

  const pct = base === null || base === 0 ? null : Math.round((count / base) * 100)
  return (
    <td className="text-end">
      <button
        type="button"
        onClick={() => onOpen(label, stage, members)}
        className="link link-hover tabular-nums"
        title={`See who's in ${label} — ${STAGE_LABELS[stage]}`}
      >
        {count}
        {pct !== null && (
          <span className="text-base-content/50 text-xs ms-1.5">({pct}%)</span>
        )}
      </button>
    </td>
  )
}

export default function Funnel({ cohorts, weeks, instrumented_since }: FunnelProps) {
  const segmentModalRef = useRef<HTMLDialogElement>(null)
  const [segment, setSegment] = useState<Segment | null>(null)
  const [copiedUserId, setCopiedUserId] = useState<number | null>(null)

  const totals = cohorts.reduce(
    (acc, c) => ({
      signups: acc.signups + c.signups,
      confirmed: acc.confirmed + c.confirmed,
      created: acc.created + c.created,
      published: acc.published + c.published,
      first_response: acc.first_response + c.first_response,
    }),
    { signups: 0, confirmed: 0, created: 0, published: 0, first_response: 0 }
  )
  const hasData = totals.signups > 0

  function openSegment(label: string, stage: StageKey, members: CohortMember[]) {
    setSegment({ label, stageLabel: STAGE_LABELS[stage], members: stageMembers(members, stage) })
    setCopiedUserId(null)
    segmentModalRef.current?.showModal()
  }

  // RFC 5322 mailbox format — pastes straight into a mail client's To: field.
  function handleCopyRecipient(m: CohortMember) {
    const recipient = m.name ? `${m.name} <${m.email}>` : m.email
    navigator.clipboard.writeText(recipient).then(() => {
      setCopiedUserId(m.user_id)
      setTimeout(() => setCopiedUserId(null), 2000)
    })
  }

  const allMembers = cohorts.flatMap((c) => c.members)

  return (
    <SystemAdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Activation funnel</h1>
          <p className="text-sm text-base-content/60 mt-1">
            Weekly signup cohorts and how far each has progressed. Click any count to see who's
            in it.
            {instrumented_since && ` Event data begins ${formatWeek(instrumented_since)}.`}
          </p>
        </div>
        <select
          className="select select-sm"
          value={weeks}
          onChange={(e) => router.get('/system_admin/funnel', { weeks: e.target.value }, { preserveState: true })}
        >
          {WEEK_OPTIONS.map((w) => (
            <option key={w} value={w}>Last {w} weeks</option>
          ))}
        </select>
      </div>

      <div className="card bg-base-100">
        {hasData ? (
          <div className="overflow-x-auto">
            <table className="table text-sm">
              <thead>
                <tr>
                  <th>Week of</th>
                  <th className="text-end">Signups</th>
                  {STAGES.map((s) => (
                    <th key={s.key} className="text-end">{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map((c) => {
                  const weekLabel = `Week of ${formatWeek(c.week_start)}`
                  return (
                    <tr key={c.week_start} className="hover:bg-base-200/40">
                      <td className="font-medium">{formatWeek(c.week_start)}</td>
                      <SegmentCell
                        count={c.signups}
                        base={null}
                        label={weekLabel}
                        stage="signups"
                        members={c.members}
                        onOpen={openSegment}
                      />
                      {STAGES.map((s) => (
                        <SegmentCell
                          key={s.key}
                          count={c[s.key]}
                          base={c.signups}
                          label={weekLabel}
                          stage={s.key}
                          members={c.members}
                          onOpen={openSegment}
                        />
                      ))}
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="font-medium">
                  <td>Total</td>
                  <SegmentCell
                    count={totals.signups}
                    base={null}
                    label="All weeks"
                    stage="signups"
                    members={allMembers}
                    onOpen={openSegment}
                  />
                  {STAGES.map((s) => (
                    <SegmentCell
                      key={s.key}
                      count={totals[s.key]}
                      base={totals.signups}
                      label="All weeks"
                      stage={s.key}
                      members={allMembers}
                      onOpen={openSegment}
                    />
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="card-body items-center text-center py-16">
            <span className="iconify lucide--filter size-16 text-base-content/20 mb-4" />
            <h2 className="text-lg font-semibold">No signups in this window</h2>
            <p className="text-base-content/50 max-w-sm">
              Cohorts appear here as new signups arrive.
              {instrumented_since
                ? ` Funnel events have been recorded since ${formatWeek(instrumented_since)}.`
                : ' No funnel events have been recorded yet.'}
            </p>
          </div>
        )}
      </div>

      <div role="alert" className="alert alert-soft alert-info text-sm">
        <span className="iconify lucide--info size-4" />
        <span>
          Percentages are of each cohort's signups. Stages can lag — a recent cohort's numbers
          will keep moving as its users activate. Sample surveys are excluded throughout.
        </span>
      </div>

      {/* Segment membership modal */}
      <dialog ref={segmentModalRef} className="modal">
        <div className="modal-box max-w-2xl">
          <div className="mb-4">
            <h3 className="text-lg font-bold">
              {segment?.label} — {segment?.stageLabel}
              <span className="badge badge-ghost badge-sm ms-2 tabular-nums">
                {segment?.members.length ?? 0}
              </span>
            </h3>
          </div>

          {segment && segment.members.length > 0 ? (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="table table-sm text-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Job title</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {segment.members.map((m) => (
                    <tr key={m.user_id}>
                      <td>
                        {m.name ? (
                          <Link
                            href={`/system_admin/users/${m.user_id}`}
                            className="link link-hover font-medium"
                          >
                            {m.name}
                          </Link>
                        ) : (
                          <span className="text-base-content/40">—</span>
                        )}
                      </td>
                      <td>
                        <Link
                          href={`/system_admin/users/${m.user_id}`}
                          className="link link-hover"
                        >
                          {m.email}
                        </Link>
                      </td>
                      <td>
                        {m.account_id ? (
                          <Link
                            href={`/system_admin/accounts/${m.account_id}`}
                            className="link link-hover"
                          >
                            {m.account_name}
                          </Link>
                        ) : (
                          <span className="text-base-content/40">—</span>
                        )}
                      </td>
                      <td>
                        {m.job_title || <span className="text-base-content/40">—</span>}
                      </td>
                      <td className="text-end">
                        <button
                          onClick={() => handleCopyRecipient(m)}
                          className="btn btn-square btn-ghost btn-sm"
                          title={m.name ? `Copy "${m.name} <${m.email}>"` : `Copy "${m.email}"`}
                        >
                          <span className={`iconify ${copiedUserId === m.user_id ? 'lucide--check' : 'lucide--copy'} size-4`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-base-content/50">
              Nobody in this segment (or its users have since been deleted).
            </p>
          )}

          <div className="modal-action">
            <form method="dialog">
              <button className="btn btn-ghost btn-sm">Close</button>
            </form>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </SystemAdminLayout>
  )
}
